import React, { useEffect, useRef, useState } from 'react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import toast from 'react-hot-toast';

// Idempotency: one key per modal mount. A double-tap or network retry reuses
// the same key, so the backend returns the existing record instead of a dupe.
const newClientKey = () =>
  (window.crypto?.randomUUID?.() ?? `ck-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const defaultExpenseForm = {
  occurred_on: new Date().toISOString().split('T')[0],
  description: '',
  category: 'General',
  total_cost: '',
  currency: 'CAD',
  split_type: 'equal',
  paid_by: 'navjeev',
  kate_share: '',
  nav_share: '',
  kate_percentage: '',
  nav_percentage: '',
  notes: '',
};

const CURRENCIES = [
  { code: 'CAD', flag: '🇨🇦' },
  { code: 'USD', flag: '🇺🇸' },
  { code: 'SGD', flag: '🇸🇬' },
  { code: 'EUR', flag: '🇪🇺' },
  { code: 'GBP', flag: '🇬🇧' },
  { code: 'MXN', flag: '🇲🇽' },
];

// ─── touch targets ───────────────────────────────────────────────────────────
// Every control in this modal has to clear a 44x44 CSS-pixel hit area (Apple
// HIG / WCAG 2.5.5 Target Size) and every text field has to render at >=16px,
// because iOS Safari zooms the whole page when you focus a smaller one.
//
// The hit area is real, not decorative: `min-h-[44px]` plus `inline-flex
// items-center` makes the *box* 44px tall even when the label inside is 12px
// text, so the padding does the work and the type stays the size it was.
const FIELD_CLASS =
  'min-h-[44px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-base outline-none transition focus:border-slate-400';
const TEXTAREA_CLASS =
  'min-h-[100px] w-full rounded-3xl border border-slate-200 px-4 py-3 text-base outline-none transition focus:border-slate-400';
const CLOSE_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border border-slate-200 px-4 text-sm text-slate-500 transition hover:border-slate-300 hover:bg-slate-50';
const SECONDARY_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50';
const PRIMARY_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60';
// The disclosure keeps its quiet 12px label; only the box around it grows.
const DISCLOSURE_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] w-fit items-center justify-start rounded-xl px-1 text-left text-xs font-medium text-slate-400 transition hover:text-slate-600';

const ExpenseModal = ({ editingRecord, initialForm, onClose, onSaved }) => {
  const [expenseForm, setExpenseForm] = useState(initialForm || defaultExpenseForm);
  const [submitting, setSubmitting] = useState(false);
  const [showAdvancedSplit, setShowAdvancedSplit] = useState((initialForm?.split_type || 'equal') !== 'equal');
  const modalRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const getFocusable = () => Array.from(modal.querySelectorAll(focusableSelectors));

    // Focus first element
    const focusable = getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const elements = getFocusable();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const clientKeyRef = useRef(newClientKey());

  const saveExpense = async (event) => {
    event.preventDefault();
    const total = Number(expenseForm.total_cost);
    if (expenseForm.split_type === 'custom') {
      const kate = Number(expenseForm.kate_share);
      const nav = Number(expenseForm.nav_share);
      if (Number.isNaN(kate) || Number.isNaN(nav) || Math.round((kate + nav) * 100) !== Math.round(total * 100)) {
        toast.error('Custom shares must add up to the total.');
        return;
      }
    }
    if (expenseForm.split_type === 'percentage') {
      const katePct = Number(expenseForm.kate_percentage);
      const navPct = Number(expenseForm.nav_percentage);
      if (Number.isNaN(katePct) || Number.isNaN(navPct) || Math.round((katePct + navPct) * 100) !== 10000) {
        toast.error('Percentages must add up to 100%.');
        return;
      }
    }
    setSubmitting(true);
    const payload = {
      ...expenseForm,
      client_key: editingRecord ? undefined : clientKeyRef.current,
      total_cost: total,
      kate_share: expenseForm.kate_share === '' ? null : Number(expenseForm.kate_share),
      nav_share: expenseForm.nav_share === '' ? null : Number(expenseForm.nav_share),
      kate_percentage: expenseForm.kate_percentage === '' ? null : Number(expenseForm.kate_percentage),
      nav_percentage: expenseForm.nav_percentage === '' ? null : Number(expenseForm.nav_percentage),
    };
    if (expenseForm.split_type === 'equal') {
      payload.kate_share = null;
      payload.nav_share = null;
      payload.kate_percentage = null;
      payload.nav_percentage = null;
    }
    if (expenseForm.split_type === 'percentage') {
      payload.kate_share = null;
      payload.nav_share = null;
    }

    try {
      const endpoint = editingRecord ? `${ENDPOINTS.EXPENSES_ENTRIES}/${editingRecord.id}` : ENDPOINTS.EXPENSES_ENTRIES;
      const method = editingRecord ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) {
        // The service rejects invalid money with a specific reason (e.g.
        // "total_cost must be a whole number of cents"). Show it — a generic
        // failure leaves you guessing which field was wrong.
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save expense');
      }
      const result = await response.json();
      onSaved(result.dashboard);
      onClose();
      toast.success(editingRecord ? 'Expense updated.' : 'Expense added.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Could not save expense.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4 backdrop-blur-sm">
      <form
        ref={modalRef}
        onSubmit={saveExpense}
        className="w-full rounded-t-3xl sm:rounded-[32px] sm:max-w-2xl border border-white/70 bg-white p-6 shadow-2xl max-h-[92dvh] overflow-y-auto"
      >
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              {editingRecord ? 'Edit expense' : 'Add expense'}
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Shared expense</h2>
          </div>
          <button type="button" onClick={onClose} className={CLOSE_BUTTON_CLASS}>
            Close
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-medium text-slate-600">Description</span>
            <input className={FIELD_CLASS} value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} required />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Date</span>
            <input type="date" className={FIELD_CLASS} value={expenseForm.occurred_on} onChange={(e) => setExpenseForm({ ...expenseForm, occurred_on: e.target.value })} required />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Category</span>
            <input className={FIELD_CLASS} value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} placeholder="Category" />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Total amount</span>
            <input type="number" min="0" step="0.01" className={FIELD_CLASS} value={expenseForm.total_cost} onChange={(e) => setExpenseForm({ ...expenseForm, total_cost: e.target.value })} placeholder="Total amount" required />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Currency</span>
            <select className={FIELD_CLASS} value={expenseForm.currency} onChange={(e) => setExpenseForm({ ...expenseForm, currency: e.target.value })} required>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Who paid</span>
            <select className={FIELD_CLASS} value={expenseForm.paid_by} onChange={(e) => setExpenseForm({ ...expenseForm, paid_by: e.target.value })}>
              <option value="navjeev">Navjeev paid</option>
              <option value="kate">Kate paid</option>
            </select>
          </label>
          <div className="grid gap-2 md:col-span-2">
            <button
              type="button"
              onClick={() => {
                const next = !showAdvancedSplit;
                setShowAdvancedSplit(next);
                if (!next) setExpenseForm((f) => ({ ...f, split_type: 'equal', kate_share: '', nav_share: '', kate_percentage: '', nav_percentage: '' }));
              }}
              className={DISCLOSURE_BUTTON_CLASS}
            >
              {showAdvancedSplit ? 'Hide advanced ▲' : 'Advanced split options ▾'}
            </button>
            {showAdvancedSplit ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-sm font-medium text-slate-600">Split type</span>
                  <select
                    className={FIELD_CLASS}
                    value={expenseForm.split_type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setExpenseForm({
                        ...expenseForm,
                        split_type: nextType,
                        kate_share: nextType === 'custom' ? expenseForm.kate_share : '',
                        nav_share: nextType === 'custom' ? expenseForm.nav_share : '',
                        kate_percentage: nextType === 'percentage' ? (expenseForm.kate_percentage || '50') : '',
                        nav_percentage: nextType === 'percentage' ? (expenseForm.nav_percentage || '50') : '',
                      });
                    }}
                  >
                    <option value="equal">Equal</option>
                    <option value="percentage">Percentage</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <div />
                {expenseForm.split_type === 'custom' ? (
                  <>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium text-slate-600">Kate share</span>
                      <input type="number" min="0" step="0.01" className={FIELD_CLASS} value={expenseForm.kate_share} onChange={(e) => setExpenseForm({ ...expenseForm, kate_share: e.target.value })} placeholder="Kate share" required />
                    </label>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium text-slate-600">Nav share</span>
                      <input type="number" min="0" step="0.01" className={FIELD_CLASS} value={expenseForm.nav_share} onChange={(e) => setExpenseForm({ ...expenseForm, nav_share: e.target.value })} placeholder="Nav share" required />
                    </label>
                  </>
                ) : null}
                {expenseForm.split_type === 'percentage' ? (
                  <>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium text-slate-600">Kate percentage</span>
                      <input type="number" min="0" max="100" step="0.01" className={FIELD_CLASS} value={expenseForm.kate_percentage} onChange={(e) => setExpenseForm({ ...expenseForm, kate_percentage: e.target.value })} placeholder="50" required />
                    </label>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium text-slate-600">Nav percentage</span>
                      <input type="number" min="0" max="100" step="0.01" className={FIELD_CLASS} value={expenseForm.nav_percentage} onChange={(e) => setExpenseForm({ ...expenseForm, nav_percentage: e.target.value })} placeholder="50" required />
                    </label>
                  </>
                ) : null}
                {expenseForm.split_type === 'equal' ? (
                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-500 md:col-span-2">
                    Split equally, with any odd cent assigned deterministically so the ledger still balances exactly.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-medium text-slate-600">Notes</span>
            <textarea className={TEXTAREA_CLASS} value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} placeholder="Notes" />
          </label>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className={PRIMARY_BUTTON_CLASS}>
            {submitting ? 'Saving...' : 'Save expense'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ExpenseModal;
